import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { cardAPI } from '../services/api';
import RichTextEditor from '../components/RichTextEditor';
import { BioRichTextDisplay } from '../components/bio-templates/BioMediaDisplay';
import { 
  ArrowLeft, 
  Save, 
  Eye, 
  EyeOff, 
  FileText,
  Sparkles,
  Layout,
  Type,
  List,
  Link as LinkIcon,
  Image as ImageIcon,
  Palette
} from 'lucide-react';

const ADMIN_EMAILS = ['sagarshiva111555@gmail.com', 'allgrowtechonologies@gmail.com'];

const EditBioPage = () => {
  const navigate = useNavigate();
  const { username } = useParams();
  const { toast } = useToast();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [card, setCard] = useState(null);
  const [richBio, setRichBio] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  
  const isAdmin = user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase());

  useEffect(() => {
    const loadCard = async () => {
      if (!user) {
        navigate('/');
        return;
      }
      
      try {
        const cardData = await cardAPI.getByUsername(username);
        
        // Check ownership
        if (cardData.userId !== user?.uid && !isAdmin) {
          toast({
            title: "Access Denied",
            description: "You can only edit your own cards.",
            variant: "destructive"
          });
          navigate('/my-cards');
          return;
        }
        
        setCard(cardData);
        setRichBio(cardData.richBio || '');
        setLoading(false);
      } catch (error) {
        console.error('Error loading card:', error);
        toast({
          title: "Error",
          description: "Failed to load card data.",
          variant: "destructive"
        });
        navigate('/my-cards');
      }
    };
    
    loadCard();
  }, [username, user, navigate, toast, isAdmin]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await cardAPI.update(username, { richBio });
      
      // Clear cache
      try {
        const cacheManager = (await import('../utils/cache')).default;
        cacheManager.clearAll();
        const { invalidateUserCardCache } = await import('../hooks/useUserCard');
        invalidateUserCardCache();
      } catch (e) {
        console.error('Cache clear error:', e);
      }
      
      toast({
        title: "Bio Saved!",
        description: "Your bio has been updated successfully."
      });
      
      // Navigate back to bio page
      setTimeout(() => navigate(`/bio/${username}`), 1000);
    } catch (error) {
      console.error('Error saving bio:', error);
      toast({
        title: "Error",
        description: "Failed to save bio. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(`/bio/${username}`)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-500" />
              <span className="font-semibold text-gray-900">Edit Bio</span>
              <span className="text-sm text-gray-500">for {card?.name}</span>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => setShowPreview(!showPreview)}
              className="hidden sm:flex items-center gap-2"
            >
              {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              {showPreview ? 'Edit' : 'Preview'}
            </Button>
            
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              {saving ? (
                <>
                  <svg className="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Bio
                </>
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Tips Banner */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-800 mb-1">Design Your Bio</h3>
              <p className="text-sm text-amber-700">
                Create a compelling bio with rich formatting. Use headings, lists, links, and colors to make your bio stand out.
              </p>
            </div>
          </div>
        </div>

        {/* Formatting Tips */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
            <Type className="w-5 h-5 text-blue-500 mx-auto mb-1" />
            <span className="text-xs text-gray-600">Headings & Text</span>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
            <List className="w-5 h-5 text-green-500 mx-auto mb-1" />
            <span className="text-xs text-gray-600">Bullet Lists</span>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
            <LinkIcon className="w-5 h-5 text-purple-500 mx-auto mb-1" />
            <span className="text-xs text-gray-600">Add Links</span>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
            <Palette className="w-5 h-5 text-pink-500 mx-auto mb-1" />
            <span className="text-xs text-gray-600">Colors & Style</span>
          </div>
        </div>

        {/* Editor / Preview */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Editor Column */}
          <div className={`${showPreview ? 'hidden lg:block' : ''}`}>
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
                <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Layout className="w-4 h-4 text-gray-500" />
                  Editor
                </h2>
              </div>
              <div className="p-4">
                <RichTextEditor
                  value={richBio}
                  onChange={setRichBio}
                  placeholder="Write your bio here... Use the toolbar above to format your text, add headings, lists, links, and more."
                />
              </div>
            </div>
          </div>

          {/* Preview Column */}
          <div className={`${!showPreview ? 'hidden lg:block' : ''}`}>
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
                <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Eye className="w-4 h-4 text-gray-500" />
                  Preview
                </h2>
              </div>
              <div className="p-4">
                {richBio ? (
                  <div className="bg-gradient-to-r from-amber-50 to-pink-50 rounded-xl p-4 border border-amber-200">
                    <BioRichTextDisplay content={richBio} />
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-400">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Your bio preview will appear here</p>
                    <p className="text-sm mt-1">Start typing in the editor</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Preview Toggle */}
        <div className="lg:hidden fixed bottom-4 right-4">
          <Button
            onClick={() => setShowPreview(!showPreview)}
            className="rounded-full w-14 h-14 shadow-lg bg-amber-500 hover:bg-amber-600"
          >
            {showPreview ? <EyeOff className="w-6 h-6" /> : <Eye className="w-6 h-6" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditBioPage;
